import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  GetAllCoursesFromEntityUseCaseInterface,
  GetAllEntitiesUseCaseInterface,
  GetAllNomenclaturesFromEntityUseCaseInterface,
  GetAllPersonsFromEntityUseCaseInterface,
  GetAllSemestersFromEntityUseCaseInterface,
  GetPersonByCPFUseCaseInterface,
  GetSemesterByIdUseCaseInterface,
} from 'domain/usecases';
import { NomenclaturesFromEntityListData } from 'presentation/app/modules/Entities/models/nomenclatures-from-entity-list-data';

interface IEntitiesState {
  entitiesListData: GetAllEntitiesUseCaseInterface.Result;
  // entitySettingsDetailsData: {
  //   name: string;
  // };
  coursesFromEntityListData: GetAllCoursesFromEntityUseCaseInterface.Result;
  semestersFromEntityListData: GetAllSemestersFromEntityUseCaseInterface.Result;
  updateSemesterModalData: GetSemesterByIdUseCaseInterface.Result;
  personsFromEntityListData: GetAllPersonsFromEntityUseCaseInterface.Result;
  addPersonToEntityModalData: GetPersonByCPFUseCaseInterface.Result;
  nomenclaturesFromEntityListData: NomenclaturesFromEntityListData;
}

interface IPayloadEntitiesListData
  extends PayloadAction<GetAllEntitiesUseCaseInterface.Result> {}

// interface IPayloadEntitySettingsDetailsData
//   extends PayloadAction<{ name: string }> {}

interface IPayloadCoursesFromEntityListData
  extends PayloadAction<GetAllCoursesFromEntityUseCaseInterface.Result> {}

interface IPayloadSemestersFromEntityListData
  extends PayloadAction<GetAllSemestersFromEntityUseCaseInterface.Result> {}

interface IPayloadUpdateSemesterModalData
  extends PayloadAction<GetSemesterByIdUseCaseInterface.Result> {}

interface IPayloadPersonsFromEntityListData
  extends PayloadAction<GetAllPersonsFromEntityUseCaseInterface.Result> {}

interface IPayloadAddPersonToEntityModalData
  extends PayloadAction<GetPersonByCPFUseCaseInterface.Result> {}

interface IPayloadNomenclaturesListData
  extends PayloadAction<GetAllNomenclaturesFromEntityUseCaseInterface.Result> {}

const initialState: IEntitiesState = {
  entitiesListData: null,
  // entitySettingsDetailsData: null,
  coursesFromEntityListData: [],
  semestersFromEntityListData: [],
  updateSemesterModalData: null,
  personsFromEntityListData: [],
  addPersonToEntityModalData: null,
  nomenclaturesFromEntityListData: [],
};

export const EntitiesSlice = createSlice({
  name: 'entities-slice',
  initialState,
  reducers: {
    loadEntitiesListData: (state, action: IPayloadEntitiesListData) => {
      state.entitiesListData = action.payload;
    },
    clearEntitiesListData: (state) => {
      state.entitiesListData = null;
    },
    // loadEntitySettingsDetailsData: (
    //   state,
    //   action: IPayloadEntitySettingsDetailsData,
    // ) => {
    //   state.entitySettingsDetailsData = action.payload;
    // },
    // clearEntitySettingsDetailsData: (state) => {
    //   state.entitySettingsDetailsData = null;
    // },
    loadCoursesFromEntityListData: (
      state,
      action: IPayloadCoursesFromEntityListData,
    ) => {
      state.coursesFromEntityListData = action.payload;
    },
    clearCoursesFromEntityListData: (state) => {
      state.coursesFromEntityListData = [];
    },
    loadSemestersFromEntityListData: (
      state,
      action: IPayloadSemestersFromEntityListData,
    ) => {
      state.semestersFromEntityListData = action.payload;
    },
    clearSemestersFromEntityListData: (state) => {
      state.semestersFromEntityListData = [];
    },
    loadUpdateSemesterModalData: (
      state,
      action: IPayloadUpdateSemesterModalData,
    ) => {
      state.updateSemesterModalData = action.payload;
    },
    clearUpdateSemesterModalData: (state) => {
      state.updateSemesterModalData = null;
    },
    loadPersonsFromEntityListData: (
      state,
      action: IPayloadPersonsFromEntityListData,
    ) => {
      state.personsFromEntityListData = action.payload;
    },
    clearPersonsFromEntityListData: (state) => {
      state.personsFromEntityListData = [];
    },
    loadNomenclaturesListData: (
      state,
      action: IPayloadNomenclaturesListData,
    ) => {
      const namesAndPlaceholders = [
        {
          name: 'Curso',
          singularPlaceholder: 'Curso',
          pluralPlaceholder: 'Cursos',
        },
        {
          name: 'Semestre',
          singularPlaceholder: 'Semestre',
          pluralPlaceholder: 'Semestres',
        },
        {
          name: 'Grupo',
          singularPlaceholder: 'Grupo',
          pluralPlaceholder: 'Grupos',
        },
        {
          name: 'Unidade Curricular',
          singularPlaceholder: 'Unidade Curricular',
          pluralPlaceholder: 'Unidades Curriculares',
        },
        {
          name: 'Local de Estágio',
          singularPlaceholder: 'Local de Estágio',
          pluralPlaceholder: 'Locais de Estágio',
        },
        {
          name: 'Reitor',
          singularPlaceholder: 'Reitor',
          pluralPlaceholder: 'Reitores',
        },
        {
          name: 'Coordenador',
          singularPlaceholder: 'Coordenador',
          pluralPlaceholder: 'Coordenadores',
        },
        {
          name: 'Professor',
          singularPlaceholder: 'Professor',
          pluralPlaceholder: 'Professores',
        },
        {
          name: 'Preceptor',
          singularPlaceholder: 'Preceptor',
          pluralPlaceholder: 'Preceptores',
        },
        {
          name: 'Estudante',
          singularPlaceholder: 'Estudante',
          pluralPlaceholder: 'Estudantes',
        },
      ];

      const nomenclaturesFromEntityListData: NomenclaturesFromEntityListData =
        action.payload.map((registry, index) => {
          return {
            id: registry.id || null,
            key: registry.key,
            name: namesAndPlaceholders[index].name,
            singularInput: {
              value: registry.single || '',
              placeholder: namesAndPlaceholders[index].singularPlaceholder,
            },
            pluralInput: {
              value: registry.plural || '',
              placeholder: namesAndPlaceholders[index].pluralPlaceholder,
            },
          };
        });

      state.nomenclaturesFromEntityListData = nomenclaturesFromEntityListData;
    },
    clearNomenclaturesListData: (state) => {
      state.nomenclaturesFromEntityListData = [];
    },
    loadAddPersonToEntityModalData: (
      state,
      action: IPayloadAddPersonToEntityModalData,
    ) => {
      state.addPersonToEntityModalData = action.payload;
    },
    clearAddPersonToEntityModalData: (state) => {
      state.addPersonToEntityModalData = null;
    },
  },
});

export const {
  loadCoursesFromEntityListData,
  clearCoursesFromEntityListData,
  // loadEntitySettingsDetailsData,
  // clearEntitySettingsDetailsData,
  loadSemestersFromEntityListData,
  clearSemestersFromEntityListData,
  loadUpdateSemesterModalData,
  clearUpdateSemesterModalData,
  loadPersonsFromEntityListData,
  clearPersonsFromEntityListData,
  loadAddPersonToEntityModalData,
  clearAddPersonToEntityModalData,
  loadNomenclaturesListData,
  clearNomenclaturesListData,
  loadEntitiesListData,
  clearEntitiesListData,
} = EntitiesSlice.actions;

export default EntitiesSlice.reducer;
